import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { Activity, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

const CHANNEL_COLORS = {
  Instagram: "#E1306C",
  Facebook: "#1877F2",
  TikTok: "#69C9D0",
  YouTube: "#FF0000",
  "Google Ads": "#4285F4",
  "Google Organic": "#34A853",
  "Website Normal": "#C9A55A",
  Direct: "#8A8A8A",
};

/** Last-7-days WhatsApp click breakdown — surfaces tracking on the dashboard
 *  without the user having to open /admin/tracking on every login. */
export default function TrackingWidget() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: payload } = await api.get("/tracking/daily?days=7");
        setData(payload.series || []);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Recharts wants one row per day with one key per channel.
  const { rows, channels, total } = useMemo(() => {
    const channelSet = new Set();
    let totalSum = 0;
    const flat = data.map((day) => {
      const row = {
        date: day.date.slice(5), // MM-DD
      };
      Object.entries(day.byChannel || {}).forEach(([ch, count]) => {
        row[ch] = count;
        channelSet.add(ch);
        totalSum += count;
      });
      return row;
    });
    return { rows: flat, channels: Array.from(channelSet), total: totalSum };
  }, [data]);

  return (
    <div className="border border-[#2A2A2A] p-6" data-testid="dashboard-tracking-widget">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Activity className="text-[#C9A55A]" size={16} />
            <div className="text-overline text-[#C9A55A]">WhatsApp clicks · last 7 days</div>
          </div>
          <div className="font-display text-[#F5F1EB] text-3xl mt-2" data-testid="dashboard-tracking-total">
            {total}
          </div>
        </div>
        <Link
          to="/admin/tracking"
          className="text-xs uppercase tracking-[0.18em] text-[#C9A55A] hover:text-[#F5F1EB] flex items-center gap-1"
          data-testid="dashboard-tracking-link">
          View all <ChevronRight size={14} />
        </Link>
      </div>

      {loading && <div className="text-[#8A8A8A] text-sm py-12 text-center">Loading…</div>}
      {!loading && total === 0 && (
        <div className="text-[#8A8A8A] text-sm py-12 text-center" data-testid="dashboard-tracking-empty">
          No WhatsApp clicks yet. Share your UTM links to start tracking.
        </div>
      )}
      {!loading && total > 0 && (
        <div className="h-64 w-full" data-testid="dashboard-tracking-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 6, right: 6, left: -16, bottom: 0 }}>
              <CartesianGrid stroke="#1f1f1f" vertical={false} />
              <XAxis dataKey="date" stroke="#8A8A8A" tick={{ fontSize: 11 }} />
              <YAxis stroke="#8A8A8A" tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#0C0C0C", border: "1px solid #2A2A2A", color: "#F5F1EB", fontSize: 12 }}
                cursor={{ fill: "rgba(201,165,90,0.07)" }}
              />
              <Legend wrapperStyle={{ fontSize: 11, color: "#8A8A8A" }} iconType="square" />
              {channels.map((ch) => (
                <Bar key={ch} dataKey={ch} stackId="a" fill={CHANNEL_COLORS[ch] || "#C9A55A"} radius={0} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
